/**
 * AudioUploadField — upload an audio file to Firebase Storage and show a player.
 *
 * Upload is immediate (fires on file select, not on form save).
 * Calls onUploadComplete(url, storagePath) when done so the parent can
 * persist the references to Firestore straight away.
 */

import { useRef, useState, useCallback, useEffect } from 'react';
import { Music, Upload, Trash2, X, Play, Pause } from 'lucide-react';
import { cn } from '@/utils/cn';
import { uploadAudio, deleteMedia } from '@/services/mediaService';
import {
  AUDIO_ACCEPT_STRING,
  MAX_AUDIO_SIZE_BYTES,
  formatFileSize,
  validateAudioFile,
} from '@/utils/fileUtils';
import type { Audience } from '@/types/auth.types';
import type { UploadProgress } from '@/services/mediaService';

interface AudioUploadFieldProps {
  audience:   Audience;
  dayNumber:  number;
  /** Current persisted audio URL (from Firestore) */
  audioUrl:         string | null;
  /** Current persisted storage path (from Firestore) */
  audioStoragePath: string | null;
  /** Called immediately after upload succeeds */
  onUploadComplete: (url: string, storagePath: string) => void;
  /** Called after the file is removed from Storage and Firestore fields cleared */
  onDelete:         () => void;
  disabled?:        boolean;
}

export default function AudioUploadField({
  audience,
  dayNumber,
  audioUrl,
  audioStoragePath,
  onUploadComplete,
  onDelete,
  disabled,
}: AudioUploadFieldProps) {
  const inputRef     = useRef<HTMLInputElement>(null);
  const audioRef     = useRef<HTMLAudioElement>(null);

  const [progress,   setProgress]   = useState<UploadProgress | null>(null);
  const [uploading,  setUploading]  = useState(false);
  const [error,      setError]      = useState<string | null>(null);
  const [deleting,   setDeleting]   = useState(false);
  const [isPlaying,  setIsPlaying]  = useState(false);
  const [localUrl,   setLocalUrl]   = useState<string | null>(null); // blob URL for instant preview
  const cancelRef    = useRef<(() => void) | null>(null);

  // Clean up blob URL on unmount
  useEffect(() => {
    return () => {
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [localUrl]);

  const handleFileSelect = useCallback((file: File) => {
    setError(null);
    const v = validateAudioFile(file);
    if (!v.valid) { setError(v.error); return; }

    // Create blob URL for instant preview while uploading
    const blob = URL.createObjectURL(file);
    setLocalUrl(blob);
    setUploading(true);
    setProgress({ bytesTransferred: 0, totalBytes: file.size, percentage: 0 });

    const cancel = uploadAudio(
      audience,
      dayNumber,
      file,
      p => setProgress(p),
      ({ url, storagePath }) => {
        setUploading(false);
        setProgress(null);
        onUploadComplete(url, storagePath);
      },
      err => {
        setUploading(false);
        setProgress(null);
        if (localUrl) URL.revokeObjectURL(localUrl);
        setLocalUrl(null);
        setError(err.message);
      },
    );
    cancelRef.current = cancel;
  }, [audience, dayNumber, onUploadComplete, localUrl]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleCancelUpload = () => {
    cancelRef.current?.();
    setUploading(false);
    setProgress(null);
    if (localUrl) URL.revokeObjectURL(localUrl);
    setLocalUrl(null);
  };

  const handleDelete = async () => {
    if (!audioStoragePath) { onDelete(); return; }
    setDeleting(true);
    try {
      await deleteMedia(audioStoragePath);
      if (localUrl) { URL.revokeObjectURL(localUrl); setLocalUrl(null); }
      onDelete();
    } catch {
      setError('Failed to delete audio. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) { el.pause(); } else { el.play(); }
  };

  const playbackUrl = localUrl ?? audioUrl;

  return (
    <div className="space-y-3">
      <label className="block text-sm font-medium text-surface-700">Audio File</label>

      {/* Existing / uploaded file — player */}
      {playbackUrl && (
        <div className="rounded-xl border border-surface-200 bg-surface-50 p-4 space-y-3">
          {/* Waveform placeholder + controls */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={togglePlay}
              disabled={uploading || disabled}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full
                         bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50
                         transition-colors shadow-sm"
            >
              {isPlaying
                ? <Pause className="h-4 w-4" />
                : <Play  className="h-4 w-4 ml-0.5" />
              }
            </button>

            {/* Progress bar (visual placeholder — real one from <audio>) */}
            <div className="flex-1 space-y-1">
              <div className="h-2 rounded-full bg-surface-200 overflow-hidden">
                <div className="h-2 bg-primary-400 rounded-full w-0" id={`audio-progress-${dayNumber}`} />
              </div>
              <div className="flex justify-between text-[10px] text-surface-400">
                <span id={`audio-time-${dayNumber}`}>0:00</span>
                <span id={`audio-duration-${dayNumber}`}>—</span>
              </div>
            </div>

            {!disabled && !uploading && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex h-8 w-8 items-center justify-center rounded-lg
                           text-red-400 hover:text-red-600 hover:bg-red-50
                           disabled:opacity-50 transition-colors"
                title="Remove audio"
              >
                {deleting
                  ? <span className="h-4 w-4 animate-spin border-2 border-red-400 border-t-transparent rounded-full" />
                  : <Trash2 className="h-4 w-4" />
                }
              </button>
            )}
          </div>

          {/* Native HTML5 audio element (hidden — we drive it via our controls) */}
          <audio
            ref={audioRef}
            src={playbackUrl}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
            onTimeUpdate={() => {
              const el = audioRef.current;
              if (!el) return;
              const timeEl = document.getElementById(`audio-time-${dayNumber}`);
              const durEl  = document.getElementById(`audio-duration-${dayNumber}`);
              const barEl  = document.getElementById(`audio-progress-${dayNumber}`);
              if (timeEl) timeEl.textContent = fmtTime(el.currentTime);
              if (durEl && isFinite(el.duration))  durEl.textContent  = fmtTime(el.duration);
              if (barEl && isFinite(el.duration) && el.duration > 0) {
                barEl.style.width = `${(el.currentTime / el.duration) * 100}%`;
              }
            }}
            onLoadedMetadata={() => {
              const el = audioRef.current;
              if (!el) return;
              const durEl = document.getElementById(`audio-duration-${dayNumber}`);
              if (durEl && isFinite(el.duration)) durEl.textContent = fmtTime(el.duration);
            }}
            className="hidden"
          />

          {localUrl && uploading && (
            <p className="text-xs text-amber-600">Uploading — do not navigate away…</p>
          )}
        </div>
      )}

      {/* Upload progress bar */}
      {uploading && progress && (
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-surface-500">
            <span>Uploading… {progress.percentage}%</span>
            <button type="button" onClick={handleCancelUpload}
              className="flex items-center gap-1 text-red-500 hover:text-red-700"
            >
              <X className="h-3 w-3" /> Cancel
            </button>
          </div>
          <div className="h-2 rounded-full bg-surface-200">
            <div
              className="h-2 rounded-full bg-primary-500 transition-all"
              style={{ width: `${progress.percentage}%` }}
            />
          </div>
          <p className="text-[10px] text-surface-400">
            {formatFileSize(progress.bytesTransferred)} / {formatFileSize(progress.totalBytes)}
          </p>
        </div>
      )}

      {/* Drop zone — only show when no file loaded and not uploading */}
      {!playbackUrl && !uploading && (
        <div
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => !disabled && inputRef.current?.click()}
          className={cn(
            'flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed',
            'px-6 py-10 transition-colors cursor-pointer',
            disabled
              ? 'border-surface-200 bg-surface-50 cursor-not-allowed'
              : 'border-surface-300 hover:border-primary-400 hover:bg-primary-50',
          )}
        >
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-surface-100">
            <Music className="h-6 w-6 text-surface-400" />
          </div>
          <div className="text-center">
            <p className="text-sm font-medium text-surface-700">
              {disabled ? 'No audio uploaded' : 'Drop audio here or click to browse'}
            </p>
            <p className="text-xs text-surface-400 mt-1">
              MP3, M4A, WAV, OGG · max {formatFileSize(MAX_AUDIO_SIZE_BYTES)}
            </p>
          </div>
          {!disabled && (
            <span className="flex items-center gap-1.5 text-xs font-medium text-primary-600">
              <Upload className="h-3.5 w-3.5" /> Choose file
            </span>
          )}
        </div>
      )}

      {/* Replace button (show when file exists and not uploading) */}
      {playbackUrl && !uploading && !disabled && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700
                     font-medium transition-colors"
        >
          <Upload className="h-3.5 w-3.5" /> Replace audio file
        </button>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}

      <input
        ref={inputRef}
        type="file"
        accept={AUDIO_ACCEPT_STRING}
        onChange={handleInputChange}
        className="hidden"
        disabled={disabled}
      />
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function fmtTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}
